import { cn } from "@artisan/ui";
import { type PointerEvent as ReactPointerEvent, useRef } from "react";

import type { RenderItemDraft } from "../project/model";
import { itemBox } from "./scene";

type Drag = { i: number; kind: "move" | "size"; dx: number; dy: number };

/** The site photo with every placed item as a numbered dashed outline. Drag an outline to
 * move it, drag its corner to resize it. The outline is only a guide for the model. */
export function PhotoCanvas({
  src,
  aspect,
  items,
  selected,
  onSelect,
  onChange,
  className,
}: {
  src: string;
  aspect: number;
  items: RenderItemDraft[];
  selected: number | null;
  onSelect: (i: number | null) => void;
  onChange: (i: number, patch: Partial<RenderItemDraft>) => void;
  className?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const at = (e: ReactPointerEvent) => {
    const r = box.current?.getBoundingClientRect();
    if (!r || !r.width || !r.height) return null;
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };
  const start = (e: ReactPointerEvent, i: number, kind: Drag["kind"]) => {
    e.stopPropagation();
    const p = at(e);
    const it = items[i];
    if (!p || !it) return;
    box.current?.setPointerCapture(e.pointerId);
    drag.current = { i, kind, dx: p.x - it.center_x, dy: p.y - it.center_y };
    onSelect(i);
  };
  const move = (e: ReactPointerEvent) => {
    const d = drag.current;
    const p = at(e);
    const it = d ? items[d.i] : undefined;
    if (!d || !p || !it) return;
    const c = (v: number, lo = 0, hi = 1) => Math.round(Math.min(hi, Math.max(lo, v)) * 1000) / 1000;
    if (d.kind === "move") {
      onChange(d.i, { center_x: c(p.x - d.dx), center_y: c(p.y - d.dy) });
    } else {
      // The corner follows the finger; the centre stays put.
      const w = c(Math.abs(p.x - it.center_x) * 2, 0.04, 1);
      onChange(d.i, it.mode === "surface" ? { width: w, height: c(Math.abs(p.y - it.center_y) * 2, 0.03, 1) } : { width: w });
    }
  };
  const end = () => (drag.current = null);

  return (
    <div
      ref={box}
      className={cn("relative touch-none overflow-hidden rounded-[9px] bg-muted select-none", className)}
      style={{ aspectRatio: String(aspect) }}
      onPointerDown={(e) => {
        e.stopPropagation(); // the studio canvas pans on its background, not on the photo
        onSelect(null);
      }}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
    >
      <img src={src} alt="Захиалагчийн өрөөний зураг" className="absolute inset-0 size-full object-cover" draggable={false} />
      {items.map((it, i) => {
        const { w, h } = itemBox(it, aspect);
        const on = selected === i;
        return (
          <div
            key={`${it.asset_id}-${i}`}
            role="button"
            tabIndex={0}
            aria-label={`${i + 1}. ${it.title}`}
            aria-pressed={on}
            onPointerDown={(e) => start(e, i, "move")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onSelect(i);
            }}
            className={cn(
              "absolute rounded-md border-[2.5px] border-dashed",
              it.mode === "surface" ? "bg-oak/35 bg-blend-normal opacity-90" : "bg-white/10",
              on ? "border-solid border-[#86a6ec] bg-[#86a6ec]/20" : "border-white",
            )}
            style={{
              left: `${(it.center_x - w / 2) * 100}%`,
              top: `${(it.center_y - h / 2) * 100}%`,
              width: `${w * 100}%`,
              height: `${h * 100}%`,
              transform: it.rotation_deg ? `rotate(${it.rotation_deg}deg)` : undefined,
              // A surface shows its material, so the customer sees what goes where.
              ...(it.mode === "surface" && it.thumb_url
                ? { backgroundImage: `linear-gradient(#ffffff26, #ffffff26), url("${it.thumb_url}")`, backgroundSize: "cover" }
                : {}),
            }}
          >
            <span
              className={cn(
                "absolute top-1/2 left-1/2 inline-flex h-6 -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-full p-0.5 text-[11px] font-semibold whitespace-nowrap shadow",
                on ? "bg-white pr-2.5 text-[#111114] ring-2 ring-[#86a6ec]" : "bg-[#0b0b0d]/80 text-white",
              )}
            >
              <i className={cn("grid size-5 place-items-center rounded-full font-mono text-[10px] not-italic", on ? "bg-primary text-primary-foreground" : "bg-white text-[#111114]")}>
                {i + 1}
              </i>
              {on ? <span className="max-w-40 truncate">{it.title}</span> : null}
            </span>
            {on ? (
              <span
                role="slider"
                tabIndex={-1}
                aria-label="Хэмжээг өөрчлөх"
                aria-valuenow={Math.round(it.width * 100)}
                onPointerDown={(e) => start(e, i, "size")}
                className="absolute -right-3 -bottom-3 size-6 rounded-full border-2 border-white bg-[#1d43b1] shadow"
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
